(() => {
  'use strict';
  const config = JSON.parse(document.getElementById('pf-context').textContent);
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  let accountSaved = [], csrf = '', loaded = false, saving = false;
  const dialog = document.getElementById('pf-signin-dialog');
  let lastSaveButton;
  const keys = { saved: 'shopping-saved-v1', recent: 'shopping-recent-v1' };
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,250}$/.test(value);
  const read = mode => {
    if (mode === 'saved') return accountSaved;
    try {
      const parsed = JSON.parse(localStorage.getItem(keys[mode]) || '[]');
      return Array.isArray(parsed) ? [...new Set(parsed.filter(validHandle))].slice(0, mode === 'saved' ? 50 : 12) : [];
    } catch { return []; }
  };
  const announce = text => { document.getElementById('pf-status').textContent = text; };
  const write = (mode, values) => {
    try { localStorage.setItem(keys[mode], JSON.stringify(values)); return true; }
    catch { announce('Your browser could not save this list. Allow site storage and try again.'); return false; }
  };
  const sync = () => {
    const saved = read('saved');
    document.querySelectorAll('[data-pf-save]').forEach(button => {
      const active = saved.includes(button.dataset.pfSave);
      button.setAttribute('aria-pressed', String(active));
      button.querySelector('[data-pf-save-label]').textContent = active ? 'Saved' : 'Save';
    });
    document.querySelectorAll('[data-pf-count]').forEach(counter => { counter.textContent = saved.length; });
  };
  let renderVersion = 0;
  const renderLists = async () => {
    const version = ++renderVersion;
    await Promise.all([...document.querySelectorAll('[data-pf-history]')].map(async section => {
      const mode = section.dataset.pfHistory;
      const handles = read(mode).filter(handle => mode === 'saved' || handle !== config.product);
      const grid = section.querySelector('[data-pf-history-grid]');
      const status = section.querySelector('[data-pf-history-status]');
      section.hidden = mode === 'recent' && !handles.length;
      if (!handles.length) {
        grid.replaceChildren();
        status.textContent = mode === 'saved' ? (config.customer ? (loaded ? 'No favourites yet. Tap a heart to save a product.' : 'Favourites are unavailable. Refresh the page to try again.') : 'Sign in to see your saved favourites across devices.') : '';
        if (mode === 'saved' && !config.customer) { const link = document.createElement('a'); link.href = config.login; link.textContent = ' Sign In'; status.append(link); }
        return;
      }
      status.textContent = 'Loading Products…';
      let failed = 0;
      const cards = [];
      // Keep requests bounded even with a full saved list.
      for (let start = 0; start < handles.length; start += 4) {
        const batch = await Promise.all(handles.slice(start, start + 4).map(async handle => {
          try {
            const url = new URL(`${root}products/${encodeURIComponent(handle)}`, location.origin);
            url.searchParams.set('view', 'pf-card');
            const preview = new URL(location.href).searchParams.get('preview_theme_id');
            if (preview) url.searchParams.set('preview_theme_id', preview);
            const response = await fetch(url, { signal: AbortSignal.timeout(15000) });
            if (!response.ok) throw new Error('Product unavailable');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = doc.querySelector('.product-card-wrapper');
            if (!card) throw new Error('Product unavailable');
            card.querySelectorAll('script').forEach(script => script.remove());
            const li = document.createElement('li'); li.append(card); return li;
          } catch { failed++; return null; }
        }));
        cards.push(...batch.filter(Boolean));
      }
      if (version !== renderVersion) return;
      grid.replaceChildren(...cards);
      status.replaceChildren();
      if (failed) {
        status.append(document.createTextNode('Some products could not be loaded. '));
        const retry = document.createElement('button'); retry.type = 'button'; retry.className = 'pf-save'; retry.textContent = 'Try Again';
        retry.addEventListener('click', renderLists); status.append(retry);
      }
      sync();
    }));
  };
  const request = async body => {
    const response = await fetch(`${root}apps/favourites`, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify({ ...body, csrf }) : undefined,
      signal: AbortSignal.timeout(15000)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !Array.isArray(data.saved) || typeof data.csrf !== 'string') throw new Error(data.error || 'Favourites could not be loaded. Please try again.');
    accountSaved = [...new Set(data.saved.filter(validHandle))].slice(0, 50);
    csrf = data.csrf; loaded = true;
  };
  const refresh = async () => {
    if (!config.customer || saving) return;
    try { await request(); sync(); await renderLists(); }
    catch (error) { loaded = false; announce(error.message); }
  };
  const askSignIn = (handle, button) => {
    lastSaveButton = button;
    const returnTo = new URL(location.href);
    returnTo.searchParams.set('save_favourite', handle);
    const login = new URL(config.login, location.origin);
    login.searchParams.set('return_to', returnTo.pathname + returnTo.search + returnTo.hash);
    const link = dialog.querySelector('[data-pf-signin]');
    link.href = login.href;
    link.onclick = () => { try { sessionStorage.setItem('pf-pending-favourite', handle); } catch {} };
    dialog.showModal();
  };
  dialog.addEventListener('close', () => lastSaveButton?.focus());
  dialog.querySelector('[data-pf-dismiss]').addEventListener('click', () => dialog.close());
  const save = async (handle, removing = false) => {
    if (saving) return;
    saving = true;
    const buttons = [...document.querySelectorAll('[data-pf-save]')];
    buttons.forEach(button => { button.disabled = true; });
    try {
      if (!loaded) await request();
      const previous = accountSaved;
      accountSaved = removing ? previous.filter(item => item !== handle) : [handle, ...previous.filter(item => item !== handle)];
      sync();
      try { await request({ action: removing ? 'remove' : 'save', handle }); }
      catch (error) { accountSaved = previous; loaded = false; throw error; }
      announce(removing ? 'Removed from your favourites.' : 'Saved to your account.');
      return true;
    } catch (error) { announce(error.message); return false; }
    finally { saving = false; buttons.forEach(button => { button.disabled = false; }); sync(); renderLists(); }
  };
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-pf-save]');
    if (button) {
      const handle = button.dataset.pfSave;
      if (!validHandle(handle)) return;
      if (!config.customer) { askSignIn(handle, button); return; }
      await save(handle, accountSaved.includes(handle));
    }
    if (event.target.closest('[data-pf-clear-recent]') && write('recent', [])) { renderLists(); announce('Recently viewed items cleared.'); }
  });
  window.addEventListener('storage', event => { if (event.key === keys.recent) renderLists(); });
  window.addEventListener('focus', refresh);
  document.addEventListener('shopify:section:load', () => { sync(); renderLists(); });
  if (validHandle(config.product)) write('recent', [config.product, ...read('recent').filter(item => item !== config.product)].slice(0, 12));
  sync();
  (async () => {
    if (config.customer) {
      await refresh();
      const page = new URL(location.href);
      let pending = page.searchParams.get('save_favourite');
      try { pending ||= sessionStorage.getItem('pf-pending-favourite'); } catch {}
      if (validHandle(pending) && await save(pending)) {
        try { sessionStorage.removeItem('pf-pending-favourite'); } catch {}
        page.searchParams.delete('save_favourite');
        history.replaceState(history.state, '', page.pathname + page.search + page.hash);
      }
    } else renderLists();
  })();
})();
